// /home/Hakyury/sites/admin.eight-percent.com/frontend/src/App.js
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { getOAuthRedirectUrl } from './utils/authFetch';

// Ленивая загрузка компонентов
const AdminLayout = React.lazy(() => import('./components/AdminLayout'));
const AdminHome = React.lazy(() => import('./components/AdminHome'));
const EvaAIConsole = React.lazy(() => import('./components/pages/EvaAIConsole'));
const ScenarioEditor = React.lazy(() => import('./components/pages/ScenarioEditor'));
const KnowledgeEditor = React.lazy(() => import('./components/pages/KnowledgeEditor'));
const RewardsStudio = React.lazy(() => import('./components/pages/RewardsStudio'));
const FramePartyWorkspace = React.lazy(() => import('./components/pages/FramePartyWorkspace'));
const UserList = React.lazy(() => import('./components/pages/UserList'));
const IntegrationStatus = React.lazy(() => import('./components/pages/IntegrationStatus'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    window.location.replace(getOAuthRedirectUrl());
    return null;
  }

  // проверка роли остаётся
  const isManager = user?.roleId === 2;
  const isAdmin = user?.roleId === 3;
  
  if (!isManager && !isAdmin) {
    window.location.replace('https://eight-percent.com');
    return null;
  }

  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Component
const AppContent = () => {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminHome />} />
          <Route path="users" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
          <Route path="services" element={<ProtectedRoute><IntegrationStatus /></ProtectedRoute>} />
          <Route path="ai" element={<ProtectedRoute><EvaAIConsole /></ProtectedRoute>} />
          <Route path="ai/scenarios" element={<ProtectedRoute><ScenarioEditor /></ProtectedRoute>} />
          <Route path="ai/knowledge" element={<ProtectedRoute><KnowledgeEditor /></ProtectedRoute>} />
          <Route path="rewards" element={<ProtectedRoute><RewardsStudio /></ProtectedRoute>} />
          <Route path="media-studio" element={<ProtectedRoute><FramePartyWorkspace /></ProtectedRoute>} />
          <Route path="frameparty-studio" element={<Navigate to="/media-studio?section=playlist" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <AdminProvider>
            <AppContent />
          </AdminProvider>
        </AuthProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
